import Modal from '@components/v2/ui/Modal/Modal';
import { Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { Form } from 'react-final-form';
import * as yup from 'yup';
import RFFTextField from '../../../components/ReactFinalForms/RFFTextField';
import LangEnum from '../../../lang/LangEnum';
import yupValidation from '../../../lib/yupValidation';
import { restApi } from '../../../services/api';
import snackbarStore from '../../../stores/Snackbar.store';
import teceoInputConfig from '../../../utils/TeceoInputConfig';
import { onlyNumbers } from '../../../utils/validators';
import { useTranslation } from '../../translation/TranslationContext';
import { useOrderActionsContext } from '../contexts/OrderActions.context';

export type ExportVerupCsvModalProps = {
  open: boolean;
  onClose: () => void;
};

const csvSchema = yup.object().shape({
  codeNumber: yup
    .string()
    .trim()
    .required(LangEnum.GLOBAL_REQUIRED)
    .matches(/^[0-9]+$/, LangEnum.ORDER_EXPORT_VERUP_CSV_ONLY_NUMBERS_VALIDATION_MESSAGE)
    .max(5, LangEnum.ORDER_EXPORT_VERUP_CSV_MAX_FIVE_NUMBERS_VALIDATION_MESSAGE),
});

const verupExportCsv = async (id: string, orderCode: string) => {
  if (id) {
    const response: any = await restApi.get(
      `/orders-export/${id}/verup?orderCode=${orderCode}`,
      undefined,
      {
        responseType: 'blob',
      },
    );

    if (response.ok) {
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/csv' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${response.headers['x-filename']}.csv`);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    return response;
  }
};

const validate = async (values: { codeNumber: string }) => yupValidation(csvSchema)({ ...values });

const ExportVerupCsvModal = ({ open, onClose }: ExportVerupCsvModalProps) => {
  const { translate } = useTranslation();

  const { orderRaw } = useOrderActionsContext();

  const orderId = orderRaw?.orderId;

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value = onlyNumbers(event.target.value);
    const MAX_LENGTH = 5;
    if (event.target.value?.length >= MAX_LENGTH + 1) {
      event.target.value = event.target.value.slice(0, MAX_LENGTH).toString();
    }
  };

  const handleSubmitForm = async ({ codeNumber }: { codeNumber: string }) => {
    if (!orderId) {
      return;
    }

    const response = await verupExportCsv(orderId, codeNumber);
    if (!response.ok) {
      snackbarStore.handleMessage({
        message: translate(LangEnum.ORDER_EXPORT_VERUP_CSV_DOWNLOAD_ERROR_MESSAGE),
        type: 'error',
      });
      return;
    }
    onClose();
  };

  return (
    <Form
      validate={validate}
      onSubmit={handleSubmitForm}
      render={({ form, handleSubmit, values }) => (
        <Modal
          isOpen={open}
          size="standard"
          onClose={onClose}
          onExited={() => form.reset()}
          header={{ title: LangEnum.ORDER_EXPORT_VERUP_CSV_TITLE }}
          footer={{
            primaryAction: {
              label: LangEnum.ORDER_EXPORT_VERUP_CSV_BUTTON_LABEL,
              isDisabled: !values.codeNumber,
              onClick: handleSubmit,
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography>{translate(LangEnum.ORDER_EXPORT_VERUP_CSV_MAIN_DESCRIPTION)}</Typography>
              <Typography color="textSecondary">
                {translate(LangEnum.ORDER_EXPORT_VERUP_CSV_SECONDARY_DESCRIPTION)}
              </Typography>
            </Grid>
            <Grid size={12}>
              <form onSubmit={handleSubmit}>
                <RFFTextField
                  {...teceoInputConfig}
                  name="codeNumber"
                  autoFocus
                  onInput={handleInput}
                  label={translate(LangEnum.ORDER_EXPORT_VERUP_CSV_INPUT_LABEL)}
                  inputProps={{
                    inputMode: 'numeric',
                  }}
                />
              </form>
            </Grid>
          </Grid>
        </Modal>
      )}
    />
  );
};

export default ExportVerupCsvModal;
